import React, { useState } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw,
  Settings,
  Clock,
  Users,
  Layers,
  CreditCard,
  Target,
  Plus,
  X,
  Circle,
  CheckCircle,
  PauseCircle,
  XCircle,
  Archive,
  HelpCircle
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import HelpModal from '../../components/HelpModal/HelpModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import projectStatusMasterService from '../../services/projectStatusMasterService'

const ProjectStatusMasterForm = () => {
  const navigate = useNavigate()
  const [showHelp, setShowHelp] = useState(false)
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('basicInfo')

  const [formData, setFormData] = useState({
    projectStatusCode: '',
    projectStatusName: '',
    projectStatusDescription: '',
    projectStatusCategory: 'Planning',
    projectStatusType: 'In Progress',
    statusColor: '#3B82F6',
    statusIcon: 'circle',
    isDefault: false,
    sortOrder: 0,
    allowsTaskCreation: true,
    allowsTimeTracking: true,
    allowsBilling: false,
    requiresApproval: false,
    allowedTransitions: [],
    notifyOnStatusChange: true,
    notifyStakeholders: false,
    averageDurationInStatus: 0
  })

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleArrayChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const addArrayItem = (field, defaultValue = '') => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], defaultValue]
    }))
  }

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }))
  }

  const validateForm = () => {
    if (!formData.projectStatusCode.trim()) {
      toast.error('Status Code is required')
      return false
    }
    if (!formData.projectStatusName.trim()) {
      toast.error('Status Name is required')
      return false
    }
    if (!formData.projectStatusDescription.trim()) {
      toast.error('Status Description is required')
      return false
    }
    return true
  }

  const handleSave = async () => {
    if (!validateForm()) {
      return
    }

    setLoading(true)
    try {
      toast.info('Creating Project Status Master record...')
      const response = await projectStatusMasterService.createProjectStatusMaster({
        projectStatusCode: formData.projectStatusCode.toUpperCase(),
        projectStatusName: formData.projectStatusName,
        projectStatusDescription: formData.projectStatusDescription,
        projectStatusCategory: formData.projectStatusCategory,
        projectStatusType: formData.projectStatusType,
        statusColor: formData.statusColor,
        statusIcon: formData.statusIcon,
        isDefault: formData.isDefault,
        sortOrder: formData.sortOrder,
        allowsTaskCreation: formData.allowsTaskCreation,
        allowsTimeTracking: formData.allowsTimeTracking,
        allowsBilling: formData.allowsBilling,
        requiresApproval: formData.requiresApproval,
        allowedTransitions: formData.allowedTransitions,
        notifyOnStatusChange: formData.notifyOnStatusChange,
        notifyStakeholders: formData.notifyStakeholders,
        averageDurationInStatus: formData.averageDurationInStatus
      })

      if (response.success) {
        toast.success('Project Status Master record created successfully!', { autoClose: 2000 })
        navigate('/projects/project-status-master')
      } else {
        toast.error(response.message || 'Failed to create Project Status Master record')
      }
    } catch (error) {
      console.error('Error creating Project Status Master record:', error)
      if (error.response?.status === 400 && error.response.data?.message.includes('already exists')) {
        toast.error('Status Code already exists. Please use a different code.')
      } else if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to perform this action.')
      } else {
        toast.error('Failed to create Project Status Master record. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setFormData({
      projectStatusCode: '',
      projectStatusName: '',
      projectStatusDescription: '',
      projectStatusCategory: 'Planning',
      projectStatusType: 'In Progress',
      statusColor: '#3B82F6',
      statusIcon: 'circle',
      isDefault: false,
      sortOrder: 0,
      allowsTaskCreation: true,
      allowsTimeTracking: true,
      allowsBilling: false,
      requiresApproval: false,
      allowedTransitions: [],
      notifyOnStatusChange: true,
      notifyStakeholders: false,
      averageDurationInStatus: 0
    })
    toast.info('Form reset to default values')
  }

  const handleBack = () => {
    navigate('/projects/project-status-master')
  }

  const breadcrumbItems = [
    { label: 'Projects', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Masters', path: '/projects?tab=masters', onClick: () => navigate('/projects?tab=masters') },
    { label: 'Project Status Master', path: '/projects/project-status-master', onClick: () => navigate('/projects/project-status-master') },
    { label: 'Create New', path: '/projects/project-status-master/new', isCurrent: true }
  ]

  const statusCategories = [
    'Planning',
    'Active', 
    'On Hold',
    'Completed',
    'Cancelled',
    'Archived'
  ]

  const statusTypes = [
    'Initial',
    'In Progress',
    'Final'
  ]

  const statusIcons = [
    'circle',
    'check-circle',
    'pause-circle',
    'x-circle',
    'archive',
    'play-circle'
  ]

  const getIconComponent = (iconName) => {
    switch (iconName) {
      case 'circle': return <Circle size={18} />
      case 'check-circle': return <CheckCircle size={18} />
      case 'pause-circle': return <PauseCircle size={18} />
      case 'x-circle': return <XCircle size={18} />
      case 'archive': return <Archive size={18} />
      case 'play-circle': return <CheckCircle size={18} />
      default: return <Circle size={18} />
    }
  }

  const helpContent = (
    <>
      <h3>What is Project Status Master?</h3>
      <p>Project Status Master lets you <strong>define project statuses</strong> such as Planning, Active, On Hold, and Completed. You can set category, type, color, icon, whether tasks/time/billing are allowed, approval requirement, and allowed transitions.</p>
      <h3>Tabs</h3>
      <p><strong>Basic Info</strong> – Status code, name, description, category, type, color, icon, sort order, default flag, allowed transitions.</p>
      <p><strong>Workflow</strong> – Allows task creation, time tracking, billing; requires approval; notify on status change and stakeholders.</p>
      <h3>How to use</h3>
      <p>Fill required fields (code, name, description). Use <strong>Create Status</strong> to save. Use <strong>Reset</strong> to clear or navigate back to cancel.</p>
    </>
  )

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Project Status Master Entry</h1>
          <p>Create and manage project status information</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Project Status Master Entry</h2>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'basicInfo' ? 'active' : ''}`}
            onClick={() => setActiveTab('basicInfo')}
          >
            <Settings size={18} />
            <span>Basic Information</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'workflow' ? 'active' : ''}`}
            onClick={() => setActiveTab('workflow')}
          >
            <Target size={18} />
            <span>Workflow Settings</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            <Users size={18} />
            <span>Notifications</span>
          </button>
        </div>
          
        <form className="form-content">

          {/* Basic Information Tab */}
          {activeTab === 'basicInfo' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Basic Information</h3>
              </div>
              
              {/* Row 1: Status Code, Name, Category, Type */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="projectStatusCode" className="form-label required">
                    Status Code
                  </label>
                  <input
                    id="projectStatusCode"
                    type="text"
                    value={formData.projectStatusCode}
                    onChange={(e) => handleInputChange('projectStatusCode', e.target.value)}
                    className="form-input"
                    placeholder="e.g., PLAN, ACTIVE, COMPLETED"
                    maxLength={20}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="projectStatusName" className="form-label required">
                    Status Name
                  </label>
                  <input
                    id="projectStatusName"
                    type="text"
                    value={formData.projectStatusName}
                    onChange={(e) => handleInputChange('projectStatusName', e.target.value)}
                    className="form-input"
                    placeholder="e.g., Planning, Active, Completed"
                    maxLength={100}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="projectStatusCategory" className="form-label required">
                    Status Category
                  </label>
                  <select
                    id="projectStatusCategory"
                    value={formData.projectStatusCategory}
                    onChange={(e) => handleInputChange('projectStatusCategory', e.target.value)}
                    className="form-input"
                  >
                    {statusCategories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="projectStatusType" className="form-label required">
                    Status Type
                  </label>
                  <select
                    id="projectStatusType"
                    value={formData.projectStatusType}
                    onChange={(e) => handleInputChange('projectStatusType', e.target.value)}
                    className="form-input"
                  >
                    {statusTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 2: Description */}
              <div className="form-group">
                <label htmlFor="projectStatusDescription" className="form-label required">
                  Status Description
                </label>
                <textarea
                  id="projectStatusDescription"
                  value={formData.projectStatusDescription}
                  onChange={(e) => handleInputChange('projectStatusDescription', e.target.value)}
                  className="form-textarea"
                  placeholder="Describe the status, its purpose, and when it should be used..."
                  rows={4}
                />
              </div>

              {/* Row 3: Visual Settings */}
              <div className="grid-row-3">
                <div className="form-group">
                  <label htmlFor="statusColor" className="form-label">
                    Status Color
                  </label>
                  <input
                    id="statusColor"
                    type="color"
                    value={formData.statusColor}
                    onChange={(e) => handleInputChange('statusColor', e.target.value)}
                    className="form-input"
                    style={{ height: '40px' }}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="statusIcon" className="form-label">
                    Status Icon
                  </label>
                  <select
                    id="statusIcon"
                    value={formData.statusIcon}
                    onChange={(e) => handleInputChange('statusIcon', e.target.value)}
                    className="form-input"
                  >
                    {statusIcons.map(icon => (
                      <option key={icon} value={icon}>{icon}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="sortOrder" className="form-label">
                    Sort Order
                  </label>
                  <input
                    id="sortOrder"
                    type="number"
                    value={formData.sortOrder}
                    onChange={(e) => handleInputChange('sortOrder', parseInt(e.target.value) || 0)}
                    className="form-input"
                    min="0"
                  />
                </div>
              </div>

              {/* Row 4: Default Checkbox */}
              <div className="form-group">
                <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={formData.isDefault}
                    onChange={(e) => handleInputChange('isDefault', e.target.checked)}
                  />
                  <span>Set as Default Status</span>
                </label>
              </div>
            </div>
          )}

          {/* Workflow Settings Tab */}
          {activeTab === 'workflow' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Workflow Settings</h3>
              </div>
              
              {/* Permissions */}
              <div className="grid-row-3">
                <div className="form-group">
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.allowsTaskCreation}
                      onChange={(e) => handleInputChange('allowsTaskCreation', e.target.checked)}
                    />
                    <span>Allows Task Creation</span>
                  </label>
                </div>
                
                <div className="form-group">
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.allowsTimeTracking}
                      onChange={(e) => handleInputChange('allowsTimeTracking', e.target.checked)}
                    />
                    <span>Allows Time Tracking</span>
                  </label>
                </div>
                
                <div className="form-group">
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.allowsBilling}
                      onChange={(e) => handleInputChange('allowsBilling', e.target.checked)}
                    />
                    <span>Allows Billing</span>
                  </label>
                </div>
              </div>

              {/* Approval */}
              <div className="form-group">
                <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={formData.requiresApproval}
                    onChange={(e) => handleInputChange('requiresApproval', e.target.checked)}
                  />
                  <span>Requires Approval</span>
                </label>
              </div>

              {/* Allowed Transitions */}
              <div className="form-group">
                <label className="form-label">Allowed Transitions</label>
                <div className="array-input-group">
                  {formData.allowedTransitions.map((transition, index) => (
                    <div key={index} className="array-item">
                      <input
                        type="text"
                        value={transition}
                        onChange={(e) => {
                          const newTransitions = [...formData.allowedTransitions]
                          newTransitions[index] = e.target.value
                          handleArrayChange('allowedTransitions', newTransitions)
                        }}
                        className="form-input"
                        placeholder="e.g., ACTIVE, COMPLETED"
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        onClick={() => removeArrayItem('allowedTransitions', index)}
                        className="btn-danger-small"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addArrayItem('allowedTransitions', '')}
                    className="btn-secondary-small"
                  >
                    Add Transition
                  </button>
                </div>
              </div>

              {/* Average Duration */}
              <div className="form-group">
                <label htmlFor="averageDurationInStatus" className="form-label">
                  Average Duration in Status (days)
                </label>
                <input
                  id="averageDurationInStatus"
                  type="number"
                  value={formData.averageDurationInStatus}
                  onChange={(e) => handleInputChange('averageDurationInStatus', parseInt(e.target.value) || 0)}
                  className="form-input"
                  min="0"
                />
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Notification Settings</h3>
              </div>
              
              <div className="grid-row-2">
                <div className="form-group">
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.notifyOnStatusChange}
                      onChange={(e) => handleInputChange('notifyOnStatusChange', e.target.checked)}
                    />
                    <span>Notify on Status Change</span>
                  </label>
                </div>
                
                <div className="form-group">
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.notifyStakeholders}
                      onChange={(e) => handleInputChange('notifyStakeholders', e.target.checked)}
                    />
                    <span>Notify Stakeholders</span>
                  </label>
                </div>
              </div>
            </div>
          )}

        </form>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            onClick={handleReset}
            className="btn-secondary"
            disabled={loading}
          >
            <RotateCcw size={16} />
            Reset
          </button>
          
          <button
            type="button"
            onClick={handleSave}
            className="btn-primary"
            disabled={loading}
          >
            <Save size={16} />
            {loading ? 'Creating...' : 'Create Status'}
          </button>
        </div>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Project Status Master – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default ProjectStatusMasterForm